import { ScrollView } from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import { TenantLink } from "@src/features/tenants";
import { useFleetClients, type ClientSummary } from "@visioni/a3s-monitoring/app";
import { EMPTY, formatText } from "../format";
import { fleetClientHref } from "../routes";
import { ClientNumberFilter } from "../components/ClientNumberFilter";
import { CursorPagerBar, countOf } from "../components/CursorPagerBar";
import { QueryNotice } from "../components/QueryNotice";

const COLUMNS = [
  { label: "Client", style: "clientNo" },
  { label: "Name", style: "name" },
  { label: "Locality", style: "locality" },
  { label: "Attend alarms", style: "attend" },
] as const;

/**
 * Every Patriot client in the replica, in client-number order, narrowed to
 * numbers starting with what is typed (`?clientNo=`). Clients are Patriot's
 * accounts rather than anything an a3s tenant owns, so the list spans the fleet.
 */
export function FleetClientsScreen() {
  const router = useRouter();
  const { tenantId, clientNo } = useLocalSearchParams<{ tenantId: string; clientNo?: string }>();
  const list = useFleetClients({ ...(clientNo !== undefined && { clientNo }) });
  const filtered = clientNo !== undefined && clientNo.trim() !== "";

  return (
    <View style={styles.page}>
      <View style={styles.filters}>
        <ClientNumberFilter clientNo={clientNo} onChange={(next) => router.setParams({ clientNo: next })} />
      </View>
      {list.items === undefined || list.error !== null ?
        <View style={styles.notice}>
          <QueryNotice isLoading={list.isLoading} error={list.error} onRetry={list.refetch} />
        </View>
      : list.items.length === 0 && list.nextCursor === null && !list.pager.canGoBack ?
        <View style={styles.centered}>
          <Text textStyle="sm/regular" color="textMuted">
            {filtered ? "No clients with a number starting with that" : "No clients found"}
          </Text>
        </View>
      : <View flex={1}>
          <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
            <View style={[styles.card, list.isPlaceholderData && styles.stale]}>
              <View style={[styles.row, styles.headerRow]}>
                {COLUMNS.map((column) => (
                  <Text key={column.label} textStyle="xs/semibold" color="textMuted" style={styles[column.style]}>
                    {column.label}
                  </Text>
                ))}
              </View>
              {list.items.map((client) => (
                <TenantLink key={client.clientNo} href={fleetClientHref(tenantId, client.clientNo)} style={styles.link}>
                  {({ hovered }) => <ClientCells client={client} hovered={hovered} />}
                </TenantLink>
              ))}
              {list.items.length === 0 && (
                <View style={styles.row}>
                  <Text textStyle="sm/regular" color="textMuted">
                    No more clients
                  </Text>
                </View>
              )}
            </View>
          </ScrollView>
          <CursorPagerBar
            pager={list.pager}
            nextCursor={list.nextCursor}
            isFetching={list.isFetching}
            summary={countOf(list.items.length, "client")}
          />
        </View>
      }
    </View>
  );
}

function ClientCells({ client, hovered }: { client: ClientSummary; hovered: boolean }) {
  const locality = [client.city, client.state].filter((part) => part !== undefined && part !== "").join(", ");
  return (
    <View style={[styles.row, hovered && styles.hovered]}>
      <Text textStyle="sm/semibold" color="text" style={styles.clientNo}>
        {client.clientNo}
      </Text>
      <Text textStyle="sm/regular" color="text" style={styles.name} numberOfLines={2}>
        {formatText(client.name)}
      </Text>
      <Text textStyle="xs/regular" color="text" style={styles.locality} numberOfLines={1}>
        {formatText(locality)}
      </Text>
      <Text textStyle="xs/regular" color="text" style={styles.attend}>
        {client.attendAlarm === undefined ?
          EMPTY
        : client.attendAlarm ?
          "Yes"
        : "No"}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create((theme) => ({
  page: { flex: 1 },
  filters: {
    paddingHorizontal: theme.spacing.xl,
    paddingTop: theme.spacing.xl,
  },
  notice: { paddingHorizontal: theme.spacing.xl },
  centered: { flex: 1, alignItems: "center", justifyContent: "center", padding: theme.spacing["4xl"] },
  scroll: { flex: 1 },
  scrollContent: { padding: theme.spacing.xl },
  card: {
    backgroundColor: theme.colors.surfaceCard,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.xl,
    overflow: "hidden",
  },
  stale: { opacity: 0.5 },
  link: { cursor: "pointer" },
  row: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.lg,
    paddingHorizontal: theme.spacing.xl,
    paddingVertical: theme.spacing.lg,
    borderTopWidth: 1,
    borderColor: theme.colors.borderCard,
  },
  headerRow: { borderTopWidth: 0, backgroundColor: theme.colors.surfaceCardInner },
  hovered: { backgroundColor: theme.colors.surfaceCardInner },
  clientNo: { width: 130 },
  name: { flex: 3, minWidth: 200 },
  locality: { flex: 1, minWidth: 140 },
  attend: { width: 100 },
}));
